/**
 * Document 2 · Phase 9.4 — Admin Workspace Settings.
 *
 * Single admin-only page with 6 tabs:
 *   1. Profile & Signature (combined)
 *   2. Industry Architect  (link-out)
 *   3. Brand Voice         (seller_context.brand_voice + seller_context.positioning)
 *   4. Workspace Defaults  (currency, date format, scheduling placeholder)
 *   5. AI Usage & Caps     (per-rep + workspace caps editor)
 *   6. Billing             (Stripe placeholder)
 *
 * Non-admin users are redirected to /dashboard.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import BillingAdminPanel from '../components/billing/BillingAdminPanel';
import UserBillingPanel from '../components/billing/UserBillingPanel';
import BillingOverridesPanel from '../components/billing/BillingOverridesPanel';
import ScheduledEmailsPanel from '../components/email/ScheduledEmailsPanel';
import {
  User, PenTool, BookOpen, MessageSquareQuote, Sliders, Sparkles, CreditCard,
  ExternalLink, Save, Loader2, Shield, CalendarClock,
} from 'lucide-react';


const SURFACES_HELPER = [
  { id: 'vision_proposal', label: 'Vision Proposal' },
  { id: 'vision_proposal_autofill', label: 'Vision Proposal · Auto-fill' },
  { id: 'outreach_email', label: 'Outreach Email' },
  { id: 'call_clarity_brief', label: 'Call Clarity Brief' },
  { id: 'signal_story', label: 'Signal Story' },
];


// ────────────────── Tab 1: Profile & Signature (combined) ──────────────────

function ProfileSignatureTab() {
  const { user } = useAuth();
  const api = useApi();
  const [s, setS] = useState({
    signature_name: '',
    signature_title: '',
    signature_phone: '',
    signature_email: '',
    signature_company: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/signature/settings');
        setS({
          signature_name: data.signature_name || user?.name || '',
          signature_title: data.signature_title || user?.title || '',
          signature_phone: data.signature_phone || '',
          signature_email: data.signature_email || user?.email || '',
          signature_company: data.signature_company || '',
        });
      } catch {
        // graceful fallback to user
        setS((p) => ({
          ...p,
          signature_name: user?.name || '',
          signature_email: user?.email || '',
        }));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put('/signature/settings', s);
      toast.success('Profile & signature saved');
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="ws-tab-profile">
      <CardContent className="p-5 space-y-5">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">Account</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Used across AI prompts and email signatures. Read-only fields come from your auth account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Account email</Label>
              <Input value={user?.email || ''} disabled className="rounded-sm" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Input value={(user?.role || '').replace('_', ' ').toUpperCase()} disabled className="rounded-sm" />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">Signature</h3>
          <p className="text-xs text-muted-foreground mb-3">
            This signature is attached to generated outreach emails and appears on your Vision Deck Contact Card.
          </p>

          {loading ? (
            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input
                  value={s.signature_name}
                  onChange={(e) => setS({ ...s, signature_name: e.target.value })}
                  className="rounded-sm" data-testid="ws-sig-name"
                />
              </div>
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  value={s.signature_title}
                  onChange={(e) => setS({ ...s, signature_title: e.target.value })}
                  className="rounded-sm" data-testid="ws-sig-title"
                />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  value={s.signature_phone}
                  onChange={(e) => setS({ ...s, signature_phone: e.target.value })}
                  className="rounded-sm" data-testid="ws-sig-phone"
                />
              </div>
              <div>
                <Label className="text-xs">Signature email</Label>
                <Input
                  value={s.signature_email}
                  onChange={(e) => setS({ ...s, signature_email: e.target.value })}
                  className="rounded-sm" data-testid="ws-sig-email"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Company</Label>
                <Input
                  value={s.signature_company}
                  onChange={(e) => setS({ ...s, signature_company: e.target.value })}
                  className="rounded-sm" data-testid="ws-sig-company"
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
            <div className="text-xs text-muted-foreground">
              Need to upload a headshot? Use the <Link to="/signature" className="underline text-[#B89B5E]">full Signature Editor</Link>.
            </div>
            <Button size="sm" onClick={onSave} disabled={saving || loading} className="rounded-sm" data-testid="ws-sig-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ────────────────── Tab 2: Industry Architect (link-out) ──────────────────

function IndustryArchitectTab() {
  return (
    <Card className="rounded-sm border" data-testid="ws-tab-industry">
      <CardContent className="p-8 text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B89B5E]/10">
          <BookOpen className="w-7 h-7 text-[#B89B5E]" />
        </div>
        <h3 className="font-serif text-2xl">Industry Architect</h3>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">
          Configure your seller industry, products, tiers, buyer roles, target segments, and pricing.
          This page is big — it opens in its own workspace.
        </p>
        <div className="pt-2">
          <Link to="/industry-architect">
            <Button className="rounded-sm" data-testid="ws-industry-open-btn">
              Open Industry Architect
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}


// ────────────────── Tab 3: Brand Voice ──────────────────

function BrandVoiceTab() {
  const api = useApi();
  const [brandVoice, setBrandVoice] = useState('professional');
  const [positioning, setPositioning] = useState('premium');
  const [valueProp, setValueProp] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/sales-intelligence/seller-context');
        const p = res?.profile || {};
        setFullProfile(p);
        setBrandVoice(p.brand_voice || 'professional');
        setPositioning(p.positioning || 'premium');
        setValueProp(p.value_proposition || '');
      } catch (e) {
        // ok — unconfigured
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const next = {
        ...(fullProfile || {}),
        brand_voice: brandVoice,
        positioning,
        value_proposition: valueProp,
      };
      await api.put('/sales-intelligence/seller-context', next);
      toast.success('Brand voice saved');
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="ws-tab-brand-voice">
      <CardContent className="p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">Brand Voice</h3>
          <p className="text-xs text-muted-foreground">
            Used by every AI prompt (Vision Proposal, Outreach Email, Signal Story, Call Brief) to match your tone.
          </p>
        </div>
        {loading ? (
          <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Voice</Label>
              <Select value={brandVoice} onValueChange={setBrandVoice}>
                <SelectTrigger className="rounded-sm" data-testid="ws-brand-voice-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="consultative">Consultative</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="warm">Warm & Personal</SelectItem>
                  <SelectItem value="bold">Bold & Confident</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Positioning</Label>
              <Select value={positioning} onValueChange={setPositioning}>
                <SelectTrigger className="rounded-sm" data-testid="ws-positioning-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="mid-tier">Mid-tier</SelectItem>
                  <SelectItem value="value">Value / Budget</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Value proposition</Label>
              <Textarea
                rows={3}
                value={valueProp}
                onChange={(e) => setValueProp(e.target.value)}
                placeholder="One sentence that captures what you sell, who it's for, and why it matters."
                className="rounded-sm"
                data-testid="ws-value-prop-input"
              />
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={onSave} disabled={saving || loading} className="rounded-sm" data-testid="ws-brand-voice-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ────────────────── Tab 4: Workspace Defaults ──────────────────

function WorkspaceDefaultsTab() {
  return (
    <Card className="rounded-sm border" data-testid="ws-tab-defaults">
      <CardContent className="p-5 space-y-5">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">Workspace Defaults</h3>
          <p className="text-xs text-muted-foreground">
            Defaults applied to new opportunities, decks, and scheduled outreach.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Currency</Label>
            <Select defaultValue="USD">
              <SelectTrigger className="rounded-sm" data-testid="ws-defaults-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD — US Dollar</SelectItem>
                <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
                <SelectItem value="EUR">EUR — Euro</SelectItem>
                <SelectItem value="GBP">GBP — British Pound</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date format</Label>
            <Select defaultValue="MDY">
              <SelectTrigger className="rounded-sm" data-testid="ws-defaults-date">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MDY">MM/DD/YYYY</SelectItem>
                <SelectItem value="DMY">DD/MM/YYYY</SelectItem>
                <SelectItem value="YMD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Timezone</Label>
            <Input defaultValue="America/New_York" disabled className="rounded-sm font-mono" />
          </div>
          <div>
            <Label className="text-xs">Default fiscal quarter</Label>
            <Select defaultValue="calendar">
              <SelectTrigger className="rounded-sm" data-testid="ws-defaults-quarter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calendar">Calendar year (Jan–Dec)</SelectItem>
                <SelectItem value="fiscal-apr">Fiscal year starts April</SelectItem>
                <SelectItem value="fiscal-jul">Fiscal year starts July</SelectItem>
                <SelectItem value="fiscal-oct">Fiscal year starts October</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="pt-2">
          <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-800 bg-amber-50">
            Read-only preview — wiring these into opportunity forms lands in the next release.
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}


// ────────────────── Tab 5: AI Usage & Caps ──────────────────

function AIUsageCapsTab() {
  const api = useApi();
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai-usage/caps');
      setCaps(res);
    } catch (e) {
      toast.error(e?.message || 'Failed to load caps');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateCap = (kind, surface, value) => {
    setCaps((prev) => ({
      ...prev,
      [kind]: { ...prev[kind], [surface]: Math.max(0, parseInt(value, 10) || 0) },
    }));
  };

  const onSave = async () => {
    if (!caps) return;
    setSaving(true);
    try {
      const res = await api.put('/ai-usage/caps', {
        per_rep: caps.per_rep,
        workspace: caps.workspace,
      });
      setCaps(res);
      toast.success('Caps updated');
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-sm border" data-testid="ws-tab-ai-caps">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">AI Usage & Caps</h3>
            <p className="text-xs text-muted-foreground">
              Per-rep caps throttle a single user each day. Workspace caps are a ceiling across all reps.
            </p>
          </div>
          <Link to="/ai-usage">
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="ws-caps-open-dashboard">
              Open AI Usage Dashboard
              <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
        {loading || !caps ? (
          <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ws-caps-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                  <th className="py-2 pr-4">Surface</th>
                  <th className="py-2 pr-4">Per-rep / day</th>
                  <th className="py-2">Workspace / day</th>
                </tr>
              </thead>
              <tbody>
                {SURFACES_HELPER.map(({ id, label }) => (
                  <tr key={id} className="border-b last:border-b-0">
                    <td className="py-2.5 pr-4 font-medium">{label}</td>
                    <td className="py-2.5 pr-4">
                      <Input
                        type="number"
                        min={0}
                        value={caps.per_rep[id] ?? 0}
                        onChange={(e) => updateCap('per_rep', id, e.target.value)}
                        className="rounded-sm h-8 w-[120px]"
                        data-testid={`ws-caps-per-rep-${id}`}
                      />
                    </td>
                    <td className="py-2.5">
                      <Input
                        type="number"
                        min={0}
                        value={caps.workspace[id] ?? 0}
                        onChange={(e) => updateCap('workspace', id, e.target.value)}
                        className="rounded-sm h-8 w-[120px]"
                        data-testid={`ws-caps-workspace-${id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={onSave} disabled={saving || loading} className="rounded-sm" data-testid="ws-caps-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save caps
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ────────────────── Tab 6: Billing (user panel + admin pricing) ──────────────────

function BillingTab() {
  const { user } = useAuth();
  const isAdmin = user && (user.is_admin || user.role === 'admin');
  return (
    <div className="space-y-5">
      {/* Every user sees their own subscription state */}
      <UserBillingPanel />
      {/* Admins additionally see the workspace pricing controls below */}
      {isAdmin && <BillingAdminPanel />}
    </div>
  );
}


// ────────────────── Page ──────────────────

export default function WorkspaceSettings() {
  const { user } = useAuth();
  const isAdmin = user && (user.is_admin || user.role === 'admin');

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="workspace-settings-page">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sliders className="w-5 h-5 text-[#B89B5E]" />
            <h1 className="font-serif text-3xl sm:text-4xl">Workspace Settings</h1>
            <Badge variant="outline" className="rounded-sm border-[#B89B5E]/50 text-[#B89B5E] text-[10px]">Admin</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Everything that shapes how your AI speaks, what it's allowed to spend, and how invoices are handled.
          </p>
        </div>

        <Tabs defaultValue="profile" className="w-full" data-testid="ws-tabs">
          <TabsList className="flex flex-wrap h-auto w-full justify-start rounded-sm bg-muted/40 p-1 gap-1">
            <TabsTrigger value="profile" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-profile">
              <User className="w-3.5 h-3.5 mr-1.5" /> Profile & Signature
            </TabsTrigger>
            <TabsTrigger value="industry" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-industry">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Industry Architect
            </TabsTrigger>
            <TabsTrigger value="brand" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-brand">
              <MessageSquareQuote className="w-3.5 h-3.5 mr-1.5" /> Brand Voice
            </TabsTrigger>
            <TabsTrigger value="defaults" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-defaults">
              <Sliders className="w-3.5 h-3.5 mr-1.5" /> Workspace Defaults
            </TabsTrigger>
            <TabsTrigger value="ai-caps" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-ai-caps">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Usage & Caps
            </TabsTrigger>
            <TabsTrigger value="billing" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-billing">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Billing
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-scheduled">
              <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Scheduled
            </TabsTrigger>
            <TabsTrigger value="overrides" className="rounded-sm data-[state=active]:bg-background" data-testid="ws-tabtrigger-overrides">
              <Shield className="w-3.5 h-3.5 mr-1.5" /> Overrides
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4"><ProfileSignatureTab /></TabsContent>
          <TabsContent value="industry" className="mt-4"><IndustryArchitectTab /></TabsContent>
          <TabsContent value="brand" className="mt-4"><BrandVoiceTab /></TabsContent>
          <TabsContent value="defaults" className="mt-4"><WorkspaceDefaultsTab /></TabsContent>
          <TabsContent value="ai-caps" className="mt-4"><AIUsageCapsTab /></TabsContent>
          <TabsContent value="billing" className="mt-4"><BillingTab /></TabsContent>
          <TabsContent value="scheduled" className="mt-4"><ScheduledEmailsPanel /></TabsContent>
          <TabsContent value="overrides" className="mt-4"><BillingOverridesPanel /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
